"use client";

import { useState, useEffect } from "react";

interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: string;
}

const UNIT_PRICE = 2500;
const DELIVERY_FEE = 200;

const orderProducts = [
  { id: "belladonna", name: "Belladonna", detail: "Pour Femme · Eau de Parfum", price: UNIT_PRICE },
  { id: "wolfsbane", name: "Wolfsbane", detail: "Pour Homme · Eau de Parfum", price: UNIT_PRICE },
];

const pkr = (n: number) => `PKR ${n.toLocaleString("en-PK")}`;

export default function OrderModal({ isOpen, onClose, product }: OrderModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    notes: "",
  });
  const [selectedProduct, setSelectedProduct] = useState(product || "Belladonna");
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const subtotal = UNIT_PRICE * quantity;
  const total = subtotal + DELIVERY_FEE;

  // Sync selection with a newly passed product (adjusted during render)
  if (isOpen && product && selectedProduct !== product) {
    setSelectedProduct(product);
  }

  useEffect(() => {
    const lenis = (window as unknown as { __lenis?: { stop(): void; start(): void } }).__lenis;

    if (isOpen) {
      document.body.style.overflow = "hidden";
      lenis?.stop();
    } else {
      document.body.style.overflow = "";
      lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      lenis?.start();
    };
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Email notifications (admin + customer) via PHP endpoint — best-effort.
    try {
      await fetch("/api/order.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product: selectedProduct,
          quantity,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          city: formData.city,
          notes: formData.notes,
        }),
      });
    } catch {
      // Ignore — the order still reaches the House through the email below.
    }

    setIsSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: "", phone: "", email: "", address: "", city: "", notes: "" });
      setQuantity(1);
      onClose();
    }, 2800);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] overflow-y-auto overscroll-contain"
      data-lenis-prevent
      role="dialog"
      aria-modal="true"
      aria-label={product || "Order Now"}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#221d17]/50 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-full flex p-4">
        <div className="relative w-full max-w-3xl m-auto bg-[#fdfbf7] border border-[#221d17]/8 shadow-[0_40px_90px_rgba(122,100,66,0.3)] animate-scaleIn">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-10 w-9 h-9 flex items-center justify-center text-[#6f6759]/60 hover:text-[#8a6a2c] hover:bg-[#c9a84c]/10 transition-colors"
          aria-label="Close"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {submitted ? (
          <div
            className="relative p-12 md:p-16 text-center overflow-hidden"
            style={{
              background: "radial-gradient(120% 120% at 50% 0%, #1c140b 0%, #0d0a07 60%, #0a0805 100%)",
            }}
          >
            {/* Ambient light */}
            <div
              className="pointer-events-none absolute inset-0"
              aria-hidden="true"
              style={{
                background: "radial-gradient(ellipse 60% 50% at 50% -10%, rgba(201,168,76,0.16), transparent 70%)",
              }}
            />
            <div
              className="silk left-[-12%] top-[30%] w-[22rem] h-[16rem] opacity-50 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at center, rgba(212,176,106,0.18), transparent 70%)" }}
              aria-hidden="true"
            />
            <div
              className="silk right-[-14%] bottom-[20%] w-[24rem] h-[16rem] opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at center, rgba(196,122,139,0.12), transparent 70%)", animationDelay: "-10s" }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-x-[-20%] top-1/2 h-24 -translate-y-1/2 opacity-30 pointer-events-none"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(212,176,106,0.12), transparent)",
                animation: "fogDrift 14s linear infinite",
              }}
              aria-hidden="true"
            />

            {/* Seal */}
            <div className="relative inline-flex items-center justify-center mb-8">
              <span className="absolute -inset-6 rounded-full border border-[#c9a84c]/25 animate-[glowPulse_3s_ease-in-out_infinite]" aria-hidden="true" />
              <span className="absolute -inset-10 rounded-full border border-[#c9a84c]/10 animate-[glowPulse_3s_ease-in-out_infinite_0.6s]" aria-hidden="true" />
              <div
                className="w-20 h-20 rounded-full border-2 border-[#c9a84c] flex items-center justify-center"
                style={{
                  background: "radial-gradient(circle at 35% 30%, #251b0d, #0d0905)",
                  boxShadow: "0 0 40px rgba(212,176,106,0.35), inset 0 0 24px rgba(212,176,106,0.25)",
                }}
              >
                <span className="font-serif text-4xl text-[#d4b06a]" style={{ textShadow: "0 0 18px rgba(212,176,106,0.6)" }}>
                  S
                </span>
              </div>
            </div>

            <p className="text-[10px] tracking-[0.4em] uppercase text-[#c8a65a] mb-4">
              The Night Remembers Your Name
            </p>
            <h3 className="font-serif text-4xl md:text-5xl text-[#ece5d4] mb-5" style={{ textShadow: "0 0 30px rgba(212,176,106,0.2)" }}>
              Summoned
            </h3>
            <p className="max-w-md mx-auto font-serif italic text-base text-[#d8cfc0]/90 leading-relaxed">
              Your order has been accepted by the House of Stygian. Our courier shall find you, and the night shall keep your
              secret until the glass is in your hands.
            </p>
            <div className="mt-8 mx-auto w-16 h-px" style={{ background: "linear-gradient(90deg, transparent, #c8a65a, transparent)" }} />
            <p className="mt-4 text-[9px] tracking-[0.3em] uppercase text-[#7a6a4f]">
              A reply approaches — watch your phone
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="p-8 md:p-12 pb-6 border-b border-[#221d17]/8">
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-3">
                Place Your Order
              </p>
              <h3 className="font-serif text-3xl md:text-4xl text-[#221d17] mb-2">
                Order Now
              </h3>
              <p className="text-sm text-[#6f6759]/70">
                Choose your fragrance, fill in your details — a courier of the House will reach out to you.
              </p>
            </div>

            {/* Form fields */}
            <div className="p-8 md:p-12 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Product selection */}
              <div className="sm:col-span-2">
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  Choose Your Scent *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {orderProducts.map((p) => {
                    const selected = selectedProduct === p.name;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => setSelectedProduct(p.name)}
                        className={`group relative border px-5 py-4 text-left transition-all duration-300 ${
                          selected
                            ? "border-[#c9a84c]/70 bg-[#c9a84c]/10 shadow-[0_8px_24px_rgba(168,132,58,0.12)]"
                            : "border-[#221d17]/10 bg-white/60 hover:border-[#c9a84c]/40 hover:bg-[#c9a84c]/5"
                        }`}
                        data-cursor="pointer"
                      >
                        <span
                          className={`absolute top-3 right-3 w-2 h-2 rounded-full transition-all duration-300 ${
                            selected ? "bg-[#c9a84c] shadow-[0_0_8px_rgba(201,168,76,0.8)]" : "border border-[#221d17]/20"
                          }`}
                          aria-hidden="true"
                        />
                        <span className="block font-serif text-lg text-[#221d17]" style={{ fontWeight: 300 }}>
                          {p.name}
                        </span>
                        <span className="block text-[9px] tracking-[0.2em] uppercase text-[#6f6759]/60 mt-1">
                          {p.detail}
                        </span>
                        <span className="block font-mono text-[11px] tracking-[0.1em] text-[#8a6a2c]/85 mt-2">
                          {pkr(p.price)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quantity */}
              <div className="sm:col-span-2">
                <label className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  Quantity
                </label>
                <div className="flex items-center justify-between border border-[#221d17]/10 bg-white/60 px-4 py-3">
                  <span className="text-[9px] tracking-[0.2em] uppercase text-[#6f6759]/70">
                    {pkr(UNIT_PRICE)} per bottle
                  </span>
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-9 h-9 flex items-center justify-center border border-[#221d17]/15 text-[#6f6759]/70 hover:border-[#c9a84c]/50 hover:text-[#8a6a2c] transition-colors"
                      aria-label="Decrease quantity"
                      data-cursor="pointer"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M5 12h14" strokeLinecap="round" />
                      </svg>
                    </button>
                    <span className="font-mono text-sm tabular-nums w-6 text-center text-[#221d17]/85">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                      className="w-9 h-9 flex items-center justify-center border border-[#221d17]/15 text-[#6f6759]/70 hover:border-[#c9a84c]/50 hover:text-[#8a6a2c] transition-colors"
                      aria-label="Increase quantity"
                      data-cursor="pointer"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="order-name" className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="order-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/70 border border-[#221d17]/10 px-4 py-3.5 text-sm text-[#221d17]/80 placeholder:text-[#6f6759]/40 focus:outline-none focus:border-[#c9a84c]/50 transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="order-phone" className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="order-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/70 border border-[#221d17]/10 px-4 py-3.5 text-sm text-[#221d17]/80 placeholder:text-[#6f6759]/40 focus:outline-none focus:border-[#c9a84c]/50 transition-colors"
                  placeholder="+92 ..."
                />
              </div>

              <div>
                <label htmlFor="order-email" className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  Email
                </label>
                <input
                  type="email"
                  id="order-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-white/70 border border-[#221d17]/10 px-4 py-3.5 text-sm text-[#221d17]/80 placeholder:text-[#6f6759]/40 focus:outline-none focus:border-[#c9a84c]/50 transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="order-address" className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  Delivery Address *
                </label>
                <input
                  type="text"
                  id="order-address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/70 border border-[#221d17]/10 px-4 py-3.5 text-sm text-[#221d17]/80 placeholder:text-[#6f6759]/40 focus:outline-none focus:border-[#c9a84c]/50 transition-colors"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label htmlFor="order-city" className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  City *
                </label>
                <input
                  type="text"
                  id="order-city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full bg-white/70 border border-[#221d17]/10 px-4 py-3.5 text-sm text-[#221d17]/80 placeholder:text-[#6f6759]/40 focus:outline-none focus:border-[#c9a84c]/50 transition-colors"
                  placeholder="City"
                />
              </div>

              <div>
                <label htmlFor="order-notes" className="text-[10px] tracking-[0.3em] uppercase text-[#6f6759]/70 mb-2 block">
                  Additional Notes
                </label>
                <textarea
                  id="order-notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full bg-white/70 border border-[#221d17]/10 px-4 py-3.5 text-sm text-[#221d17]/80 placeholder:text-[#6f6759]/40 focus:outline-none focus:border-[#c9a84c]/50 transition-colors resize-none"
                  placeholder="Any special requests..."
                />
              </div>

              {/* Order summary */}
              <div className="sm:col-span-2 border border-[#c9a84c]/30 bg-[#c9a84c]/5 p-5 md:p-6">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#8a6a2c]/70 mb-4">
                  Order Summary
                </p>
                <div className="space-y-2.5 text-sm">
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[#221d17]/80">
                      {selectedProduct} × {quantity}
                    </span>
                    <span className="font-mono text-[#221d17]/85 tabular-nums">{pkr(subtotal)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="text-[#221d17]/80">Delivery — all over Pakistan</span>
                    <span className="font-mono text-[#221d17]/85 tabular-nums">{pkr(DELIVERY_FEE)}</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 pt-3 mt-2 border-t border-[#c9a84c]/25">
                    <span className="font-serif text-lg text-[#221d17]">Total</span>
                    <span className="font-mono text-lg tabular-nums text-[#a8843a]">{pkr(total)}</span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-elegant justify-center disabled:opacity-30 disabled:cursor-not-allowed sm:col-span-2"
              >
                <span>{isSubmitting ? "Sending..." : `Place Order — ${pkr(total)}`}</span>
              </button>
              <p className="text-center text-[8px] text-[#6f6759]/60">
                No advance payment needed — delivery all over Pakistan.
              </p>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
}